/**
 * VerifyPanel — 6 桁確認コード入力 UI (Issue #5996, brushup)
 *
 * - 6 個の独立した <input> で 1 桁ずつ受け取る (Stripe / Linear と同等)
 * - paste 対応 (1 つ目の input にペーストで全桁充填)
 * - 各入力で自動 focus 移動 (Backspace で前へ戻る)
 * - aria-live="polite" でエラー通知
 *
 * 親 (LoginPage) から `email` を受け取り、`onVerified(session)` で確定通知する。
 * AuthContext は LoginPage 側で provide されている前提。
 */

import { useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { AuthApiError, type AuthApiClient, type AuthResponse } from "./api";

const CODE_LENGTH = 6;

export interface VerifyPanelProps {
  email: string;
  client: AuthApiClient;
  onVerified: (session: AuthResponse) => void;
  onBack: () => void;
  onResend?: () => void | Promise<void>;
  /** test injection */
  initialCode?: string;
  invitationToken?: string;
}

type ErrKey = "invalidCode" | "expired" | "tooManyAttempts" | "unknown";

export function VerifyPanel({
  email,
  client,
  onVerified,
  onBack,
  onResend,
  initialCode = "",
  invitationToken,
}: VerifyPanelProps): JSX.Element {
  const { t } = useTranslation();
  const [digits, setDigits] = useState<string[]>(() => padDigits(initialCode));
  const [errorKey, setErrorKey] = useState<ErrKey | null>(null);
  const [pending, setPending] = useState(false);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const code = useMemo(() => digits.join(""), [digits]);
  const complete = code.length === CODE_LENGTH && /^\d{6}$/.test(code);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  const setDigit = (i: number, v: string) => {
    setErrorKey(null);
    setDigits((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
  };

  const focusInput = (i: number) => {
    const el = inputs.current[i];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const onChange = (i: number, raw: string) => {
    // accept only the LAST single digit so users can overwrite easily
    const onlyDigits = raw.replace(/\D+/g, "");
    if (!onlyDigits) {
      setDigit(i, "");
      return;
    }
    if (onlyDigits.length === 1) {
      setDigit(i, onlyDigits);
      if (i < CODE_LENGTH - 1) focusInput(i + 1);
    } else {
      // multiple chars typed (autofill / IME) — distribute starting at i
      const arr = onlyDigits.slice(0, CODE_LENGTH - i).split("");
      setDigits((prev) => {
        const next = [...prev];
        arr.forEach((c, k) => {
          next[i + k] = c;
        });
        return next;
      });
      const dest = Math.min(i + arr.length, CODE_LENGTH - 1);
      requestAnimationFrame(() => focusInput(dest));
    }
  };

  const onKeyDown = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        setDigit(i, "");
      } else if (i > 0) {
        focusInput(i - 1);
        setDigit(i - 1, "");
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusInput(i - 1);
    } else if (e.key === "ArrowRight" && i < CODE_LENGTH - 1) {
      e.preventDefault();
      focusInput(i + 1);
    } else if (e.key === "Enter" && complete) {
      e.preventDefault();
      void submit();
    }
  };

  const onPaste = (i: number, e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    const cleaned = text.replace(/\D+/g, "");
    if (!cleaned) return;
    e.preventDefault();
    const arr = cleaned.slice(0, CODE_LENGTH - i).split("");
    setDigits((prev) => {
      const next = [...prev];
      arr.forEach((c, k) => {
        next[i + k] = c;
      });
      return next;
    });
    const dest = Math.min(i + arr.length, CODE_LENGTH - 1);
    requestAnimationFrame(() => focusInput(dest));
  };

  const submit = async () => {
    if (!complete || pending) return;
    setPending(true);
    setErrorKey(null);
    try {
      const session = await client.verifyMagicLink({ email, code, ...(invitationToken ? { invitation_token: invitationToken } : {}) });
      onVerified(session);
    } catch (e) {
      setErrorKey(mapErr(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={20} className="text-brand-600" aria-hidden />
        <h2 className="text-lg font-bold text-fg-primary">{t("auth.verify.title")}</h2>
      </div>
      <p className="text-sm text-fg-secondary mb-5 leading-relaxed">
        {t("auth.verify.subtitle", { email })}
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        aria-busy={pending || undefined}
      >
        <fieldset className="mb-3">
          <legend className="block text-sm font-medium text-fg-primary mb-2">
            {t("auth.verify.codeLabel")}
          </legend>
          <div className="flex gap-2 justify-between" role="group">
            {digits.map((v, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputs.current[i] = el;
                }}
                value={v}
                inputMode="numeric"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                pattern="[0-9]*"
                maxLength={1}
                aria-label={`${t("auth.verify.codeLabel")} ${i + 1}/${CODE_LENGTH}`}
                aria-invalid={errorKey ? true : undefined}
                onChange={(e) => onChange(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                onPaste={(e) => onPaste(i, e)}
                onFocus={(e) => e.currentTarget.select()}
                disabled={pending}
                className={cn(
                  "w-11 h-12 sm:w-12 sm:h-14 rounded-md text-center text-xl font-mono",
                  "bg-canvas border placeholder:text-fg-subtle",
                  "focus-visible:shadow-focus tabular-nums",
                  errorKey
                    ? "border-danger"
                    : "border-border-default hover:border-border-strong",
                  pending && "opacity-60 cursor-not-allowed",
                )}
              />
            ))}
          </div>
        </fieldset>

        <div role="alert" aria-live="polite" className="min-h-[1.25rem] mb-3">
          {errorKey ? (
            <p className="text-sm text-danger">{t(`auth.verify.errors.${errorKey}`)}</p>
          ) : null}
        </div>

        <Button
          type="submit"
          className="w-full"
          loading={pending}
          disabled={pending || !complete}
        >
          {pending ? t("auth.verify.verifying") : t("auth.verify.submit")}
        </Button>

        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg-primary focus-visible:shadow-focus rounded px-1"
          >
            <ArrowLeft size={14} aria-hidden /> {t("auth.verify.back")}
          </button>
          {onResend ? (
            <button
              type="button"
              onClick={() => void onResend()}
              className="text-sm text-brand-700 hover:underline focus-visible:shadow-focus rounded px-1"
            >
              {t("auth.login.sentResendButton")}
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}

function padDigits(s: string): string[] {
  const arr = s.split("").slice(0, CODE_LENGTH);
  while (arr.length < CODE_LENGTH) arr.push("");
  return arr;
}

function mapErr(e: unknown): ErrKey {
  if (e instanceof AuthApiError) {
    if (e.status === 429) return "tooManyAttempts";
    if (e.code === "MAGIC_LINK_EXPIRED" || e.code === "CODE_EXPIRED") return "expired";
    if (e.code === "MAGIC_LINK_INVALID" || e.code === "INVALID_CODE" || e.status === 400)
      return "invalidCode";
    if (e.status === 401 || e.status === 403) return "invalidCode";
  }
  return "unknown";
}
